<template>
  <v-form v-model="valid" class="w-100" @submit.prevent="saveOptimiserSettings">
    <v-row class="" no-gutters align="center" style="height: 96px" align-content="space-between">
      <v-col cols="auto">
        <v-row class="pt-7 pb-0">
          <!-- Optimize according to tooltip -->
          <span class="optimiser-settings__label"> {{ tkey('optimiseAccording') }}</span>
          <AssortmentTooltip
            :title="t('tooltips.optimiseAccordingTo.title')"
            :tooltip-sections="optimiseAccordingToTooltipSections"
          />
          <v-select
            :disabled="validatingOptimiseOptions || readOnly"
            :model-value="optimiseAccordingTo"
            :items="sizeSelectItems"
            :error-messages="optimiseAccordingToErrorMessage"
            :placeholder="t('sizeTypes.noOption')"
            :append-icon="sizeTypeIconMap[optimiseAccordingTo] || '$empty-cube'"
            min-width="230"
            @update:model-value="selectSetting"
          />
        </v-row>
      </v-col>

      <v-spacer />

      <v-col cols="auto">
        <v-row align="center" class="">
          <v-col cols="auto">
            <v-row style="flex-wrap: nowrap">
              <span class="optimiser-settings__label">{{ tkey('categoryStrategy') }}</span>
              <!-- Category strategy tooltip -->
              <AssortmentTooltip
                :title="t('tooltips.categoryStrategy.title')"
                :tooltip-sections="categoryStrategyTooltipSections"
              />
            </v-row>
          </v-col>

          <v-col class="pt-0" style="max-width: 128px">
            <span>
              {{ tkey('unitVolume') }}
            </span>
            <AssortmentTextField
              v-model="unitVolume"
              :disabled="readOnly"
              :rules="rules"
              bg-color="white"
              variant="filled"
            />
          </v-col>

          <v-col class="pt-0" style="max-width: 96px">
            <span>
              {{ tkey('sales') }}
            </span>
            <AssortmentTextField
              v-model="sales"
              :disabled="readOnly"
              :rules="rules"
              bg-color="white"
              variant="filled"
            />
          </v-col>

          <v-col class="pt-0" style="min-width: 128px">
            <span>
              {{ tkey('margin')
              }}<AssortmentTooltip
                v-if="isMarginWithoutFundingEnabled"
                :title="t('general.warning')"
                :tooltip-sections="marginWithoutFundingSections"
                :width="500"
              />
            </span>
            <AssortmentTextField
              v-model="margin"
              :disabled="readOnly"
              :rules="rules"
              bg-color="white"
              variant="filled"
            />
          </v-col>

          <v-col v-if="isFreshWP" class="pt-0" style="max-width: 96px">
            <span>
              {{ tkey('shrink') }}
            </span>
            <AssortmentTextField
              v-model="shrink"
              :disabled="readOnly"
              :rules="rules"
              bg-color="white"
              variant="filled"
            />
          </v-col>

          <v-col v-if="isFreshWP" class="pt-0" style="max-width: 160px">
            <span>
              {{ tkey('shrinkFill') }}
            </span>
            <v-switch
              v-model="shrinkFill"
              :disabled="readOnly"
              color="primary"
              hide-details
              density="compact"
            />
          </v-col>

          <v-col v-if="customerSegmentsEnabled" class="pt-0">
            <span>{{ tkey('customerSegment') }}</span>
            <v-select
              :disabled="readOnly"
              :model-value="customerSegmentKey"
              :items="customerSegmentsOptions"
              @update:model-value="selectCustomerSegment"
            />
          </v-col>

          <v-col v-if="customerSegmentsEnabled" class="pt-0" style="max-width: 96px">
            <span>{{ tkey('maximumCap') }}</span>
            <AssortmentTextField
              v-model="maxCap"
              :disabled="readOnly || !customerSegmentKey"
              :rules="errors"
              bg-color="white"
              variant="filled"
              class="rtls-text-field optimiser-settings__input v-text-field__slot"
            />
          </v-col>
        </v-row>
      </v-col>

      <v-col cols="auto">
        <v-btn
          data-id-e2e="btnSaveOptimiserSetting"
          variant="flat"
          max-height="28"
          type="submit"
          action
          :disabled="isSaveDisabled"
        >
          {{ t('actions.save') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { isEqual, get, every, includes } from 'lodash';
import { mapState, mapActions } from 'pinia';
import scenarioAssortments from '@enums/scenario-assortments';
import SIZE_OPTIONS from '@enums/size-types';
import customerSegmentationMixin from '@/mixins/customer-segmentation';
import { useScenariosStore } from '@/stores/scenarios';
import { useScenarioOptimiserTooltipOptions } from '@/composables/use-tooltip-options';
import { useInputValidations } from '@/composables/input-validations';
import { useFeature } from '@/composables/use-feature';
import { useInternationalization } from '@/composables/use-internationalization';
import { useContextStore } from '@/stores/context';
import { useToolDataStore } from '@/stores/tool-data';
import { useWorkpackagesStore } from '@/stores/workpackages';

// Maximum value for the maxCap input field
const MAXIMUM_CAP = 100;

export default {
  mixins: [customerSegmentationMixin],

  props: {
    readOnly: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  setup() {
    const {
      marginWithoutFundingSections,
      categoryStrategyTooltipSections,
      optimiseAccordingToTooltipSections,
    } = useScenarioOptimiserTooltipOptions();

    const { required, isInteger, isNumber, isLessThan, isGreaterThan } = useInputValidations();

    const { isMarginWithoutFundingEnabled, customerSegmentsEnabled } = useFeature();

    const { tkey, t } = useInternationalization('scenarioAssortmentPanel.form');

    return {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isGreaterThan,
      categoryStrategyTooltipSections,
      marginWithoutFundingSections,
      optimiseAccordingToTooltipSections,
      isMarginWithoutFundingEnabled,
      customerSegmentsEnabled,
      tkey,
      t,
    };
  },

  data() {
    return {
      valid: true,
      originalValues: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.optimiseAccordingTo]: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.unitVolume]: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.bulkVolume]: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.sales]: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.margin]: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.shrink]: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.shrinkFill]: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey]: null,
      [scenarioAssortments.OPTIMISER_SETTINGS.maxCap]: null,
      rules: [this.required, this.isInteger, this.isNumber],
      optimiseAccordingToErrorMessage: [],
      validatingOptimiseOptions: false,
      sizeTypeIconMap: {
        [SIZE_OPTIONS.linearSpace]: '$linear-space',
        [SIZE_OPTIONS.cubicSpace]: '$cubic-space',
        [SIZE_OPTIONS.horizontalSpace]: '$horizontal-space',
        [SIZE_OPTIONS.frontalSpace]: '$frontal-space',
        [SIZE_OPTIONS.productCount]: '$empty-cube',
      },
      customerSegments: [],
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useContextStore, ['getClientConfig', 'getDefaultOptimiserWeights']),
    ...mapState(useWorkpackagesStore, ['isFreshWP', 'selectedWorkpackage']),

    sizeSelectItems() {
      return Object.keys(SIZE_OPTIONS).map(key => ({
        value: SIZE_OPTIONS[key],
        title: this.$t(`sizeTypes.${key}`),
      }));
    },

    customerSegmentsOptions() {
      const options = this.getCustomerSegmentsFlat().map(
        ({
          customerSegmentSchemeName,
          customerSegmentScheme,
          customerSegmentName,
          customerSegment,
        }) => ({
          title: `${customerSegmentSchemeName} - ${customerSegmentName}`,
          value: `${customerSegmentScheme}-${customerSegment}`,
        })
      );
      return [{ value: null, title: '' }, ...options];
    },

    hasDataChanges() {
      const currentValues = this.getValues();
      return !isEqual(this.originalValues, currentValues);
    },

    errors() {
      const customerSegmentKey = this[scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey];
      if (!customerSegmentKey) return [];

      return [
        value => this.isInteger(value),
        value => this.isLessThan(value, MAXIMUM_CAP),
        value => this.isGreaterThan(value, 0),
      ];
    },

    isSaveDisabled() {
      return !this.valid || !every(this.errors) || !this.hasDataChanges || this.readOnly;
    },
  },

  watch: {
    // Have to validate the values like this, because we use async validation
    // and async rules are not supported for any v-input. https://github.com/vuetifyjs/vuetify/issues/4231
    optimiseAccordingTo(v) {
      this.isOptimiseAccordingToValid(v);
    },
  },

  created() {
    this.init();
  },

  methods: {
    ...mapActions(useScenariosStore, [
      'updateOptimiserSettings',
      'setSelectedScenario',
      'hasValidProductAttributes',
    ]),
    ...mapActions(useToolDataStore, ['getCustomerSegments']),

    async init() {
      const {
        sales: defaultSales,
        margin: defaultMargin,
        unitVolume: defaultVolume,
        shrink: defaultShrink,
        shrinkFill: defaultShrinkFill,
      } = this.getDefaultOptimiserWeights;

      this[scenarioAssortments.OPTIMISER_SETTINGS.margin] = defaultMargin;
      this[scenarioAssortments.OPTIMISER_SETTINGS.sales] = defaultSales;
      this[scenarioAssortments.OPTIMISER_SETTINGS.unitVolume] = defaultVolume;
      this[scenarioAssortments.OPTIMISER_SETTINGS.shrink] = defaultShrink;
      this[scenarioAssortments.OPTIMISER_SETTINGS.shrinkFill] = defaultShrinkFill;

      this.populateFormValues(this.selectedScenario.optimiserSettings);

      this.originalValues = {
        ...this.getValues(),
        [scenarioAssortments.OPTIMISER_SETTINGS.margin]: get(
          this.selectedScenario,
          'optimiserSettings.utilityWeights.margin',
          defaultMargin
        ),
        [scenarioAssortments.OPTIMISER_SETTINGS.sales]: get(
          this.selectedScenario,
          'optimiserSettings.utilityWeights.sales',
          defaultSales
        ),
        [scenarioAssortments.OPTIMISER_SETTINGS.unitVolume]: get(
          this.selectedScenario,
          'optimiserSettings.utilityWeights.unitVolume',
          defaultVolume
        ),
        [scenarioAssortments.OPTIMISER_SETTINGS.shrink]: get(
          this.selectedScenario,
          'optimiserSettings.utilityWeights.shrink',
          defaultShrink
        ),
        [scenarioAssortments.OPTIMISER_SETTINGS.shrinkFill]: get(
          this.selectedScenario,
          'optimiserSettings.utilityWeights.shrinkFill',
          defaultShrinkFill
        ),
      };

      if (this.customerSegmentsEnabled) {
        this.customerSegments = await this.getCustomerSegments();
      }

      // Default the optimiser acc to to the workpackge fill by setting
      // Set after originalvalues is set to allow saving
      const useFillDefault = get(
        this.getClientConfig,
        'features.useWorkpackageFillForOptimiserDefault'
      );
      if (useFillDefault && !this[scenarioAssortments.OPTIMISER_SETTINGS.optimiseAccordingTo]) {
        this[scenarioAssortments.OPTIMISER_SETTINGS.optimiseAccordingTo] = get(
          this.selectedWorkpackage,
          'fillInSelection',
          null
        );
      }
    },

    getValues() {
      return {
        [scenarioAssortments.OPTIMISER_SETTINGS.optimiseAccordingTo]:
          this[scenarioAssortments.OPTIMISER_SETTINGS.optimiseAccordingTo],
        [scenarioAssortments.OPTIMISER_SETTINGS.unitVolume]:
          this[scenarioAssortments.OPTIMISER_SETTINGS.unitVolume],
        [scenarioAssortments.OPTIMISER_SETTINGS.bulkVolume]:
          this[scenarioAssortments.OPTIMISER_SETTINGS.bulkVolume],
        [scenarioAssortments.OPTIMISER_SETTINGS.sales]:
          this[scenarioAssortments.OPTIMISER_SETTINGS.sales],
        [scenarioAssortments.OPTIMISER_SETTINGS.margin]:
          this[scenarioAssortments.OPTIMISER_SETTINGS.margin],
        [scenarioAssortments.OPTIMISER_SETTINGS.shrink]:
          this[scenarioAssortments.OPTIMISER_SETTINGS.shrink],
        [scenarioAssortments.OPTIMISER_SETTINGS.shrinkFill]:
          this[scenarioAssortments.OPTIMISER_SETTINGS.shrinkFill],
        [scenarioAssortments.OPTIMISER_SETTINGS.utilityWeights]:
          this[scenarioAssortments.OPTIMISER_SETTINGS.utilityWeights],
        ...(this.customerSegmentsEnabled && {
          [scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey]:
            this[scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey],
          [scenarioAssortments.OPTIMISER_SETTINGS.maxCap]:
            this[scenarioAssortments.OPTIMISER_SETTINGS.maxCap],
        }),
      };
    },

    selectSetting(setting) {
      this.optimiseAccordingToErrorMessage = [];
      this[scenarioAssortments.OPTIMISER_SETTINGS.optimiseAccordingTo] = setting;
    },

    selectCustomerSegment(segment) {
      this[scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey] = segment;

      // Populate maxCap if customerSegmentKey has a value and maxCap is null
      if (
        this[scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey] &&
        !this[scenarioAssortments.OPTIMISER_SETTINGS.maxCap]
      ) {
        const defaultMaximumCap = get(this.getClientConfig, 'primaryCustomerFactor.maxCap');
        // Populated with the currently saved setting or fallback to the default from config
        this[scenarioAssortments.OPTIMISER_SETTINGS.maxCap] =
          get(this.selectedScenario, 'optimiserSettings.primaryCustomerFactor.maxCap') ||
          defaultMaximumCap;
      }
      // If customerSegmentKey has been unset, revert maxCap to null
      if (!this[scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey]) {
        this[scenarioAssortments.OPTIMISER_SETTINGS.maxCap] = null;
      }
    },

    populateFormValues(optimiserSettings = {}) {
      Object.keys(optimiserSettings).forEach(key => {
        const nestedSettings = [scenarioAssortments.OPTIMISER_SETTINGS.utilityWeights];
        if (this.customerSegmentsEnabled) {
          nestedSettings.push(scenarioAssortments.OPTIMISER_SETTINGS.primaryCustomerFactor);
        }
        this[scenarioAssortments.OPTIMISER_SETTINGS[key]] =
          optimiserSettings[scenarioAssortments.OPTIMISER_SETTINGS[key]];

        if (this[scenarioAssortments.OPTIMISER_SETTINGS[key]] && includes(nestedSettings, key)) {
          this.populateFormValues(optimiserSettings[key]);
        }
      });
    },

    async saveOptimiserSettings() {
      const result = await this.updateOptimiserSettings({
        id: this.selectedScenario._id,
        optimiserSettings: {
          [scenarioAssortments.OPTIMISER_SETTINGS.optimiseAccordingTo]:
            this[scenarioAssortments.OPTIMISER_SETTINGS.optimiseAccordingTo],
          [scenarioAssortments.OPTIMISER_SETTINGS.utilityWeights]: {
            [scenarioAssortments.OPTIMISER_SETTINGS.unitVolume]: parseInt(
              this[scenarioAssortments.OPTIMISER_SETTINGS.unitVolume],
              10
            ),
            [scenarioAssortments.OPTIMISER_SETTINGS.bulkVolume]:
              this[scenarioAssortments.OPTIMISER_SETTINGS.bulkVolume] === ''
                ? null
                : this[scenarioAssortments.OPTIMISER_SETTINGS.bulkVolume],
            [scenarioAssortments.OPTIMISER_SETTINGS.sales]: parseInt(
              this[scenarioAssortments.OPTIMISER_SETTINGS.sales],
              10
            ),
            [scenarioAssortments.OPTIMISER_SETTINGS.margin]: parseInt(
              this[scenarioAssortments.OPTIMISER_SETTINGS.margin],
              10
            ),
            [scenarioAssortments.OPTIMISER_SETTINGS.shrink]: parseInt(
              this[scenarioAssortments.OPTIMISER_SETTINGS.shrink],
              10
            ),
            [scenarioAssortments.OPTIMISER_SETTINGS.shrinkFill]:
              this[scenarioAssortments.OPTIMISER_SETTINGS.shrinkFill],
          },
          [scenarioAssortments.OPTIMISER_SETTINGS.primaryCustomerFactor]: this[
            scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey
          ]
            ? {
                [scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey]:
                  this[scenarioAssortments.OPTIMISER_SETTINGS.customerSegmentKey],
                [scenarioAssortments.OPTIMISER_SETTINGS.maxCap]:
                  this[scenarioAssortments.OPTIMISER_SETTINGS.maxCap],
              }
            : null,
        },
      });
      if (result.errors) return;
      if (result.data) {
        this.setSelectedScenario(result.data);
        this.init();
      }
    },

    async isOptimiseAccordingToValid(value) {
      if (!value) {
        this.optimiseAccordingToErrorMessage.push(this.$t('validationErrors.required'));
        return;
      }

      // only check if there's a value different from the saved one and not productCount
      if (
        (!this.selectedScenario.optimiserSettings && value !== SIZE_OPTIONS.productCount) ||
        (value !== this.selectedScenario.optimiserSettings.optimiseAccordingTo &&
          value !== SIZE_OPTIONS.productCount)
      ) {
        const key = Object.keys(SIZE_OPTIONS).find(k => SIZE_OPTIONS[k] === value);
        this.validatingOptimiseOptions = true;
        const { data } = await this.hasValidProductAttributes({
          scenarioId: this.selectedScenario._id,
          optimiseAccordingTo: key,
        });
        this.validatingOptimiseOptions = false;
        if (!data) {
          this.optimiseAccordingToErrorMessage.push(this.$t(`validationErrors.sizeType.${key}`));
        }
      }
    },
  },
};
</script>

<style lang="scss">
@import '@style/base/_variables.scss';

.optimiser-settings {
  width: 100%;
  padding: 0px;

  &__input {
    width: 80px;
  }

  &__label {
    color: $assortment-font-colour;
    display: inline-block;
    margin-right: 5px;
  }
}
</style>
